import React from "react";
import { Button, IconButton, Slider, Typography, Box } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";
import { resolveImageSrc } from "./ImageFocusZoomPicker";

const EFFECTS = [
  { id: "none", labelKey: "kenBurns.effects.none", defaultLabel: "Ingen" },
  { id: "spotlight", labelKey: "kenBurns.effects.spotlight", defaultLabel: "Spotlight" },
  { id: "pulse", labelKey: "kenBurns.effects.pulse", defaultLabel: "Markør" },
  { id: "blur", labelKey: "kenBurns.effects.blur", defaultLabel: "Uskarphet" },
  { id: "lens", labelKey: "kenBurns.effects.lens", defaultLabel: "Lupe" },
];

const EFFECT_DESCRIPTIONS = {
  none: "Kun jevn zoom og panorering uten ekstra visuell markering.",
  spotlight: "💡 Passer til malerier, gjenstander og portretter. Lyser opp fokusområdet og toner ned omgivelsene.",
  pulse: "🎯 Passer til kart, tekniske tegninger og gruppebilder («hvem er hvem»). Viser pulserende ringer i fokuspunktet.",
  blur: "💧 Passer til nærbilder av detaljer og inskripsjoner. Gjør omgivelsene mykt uskarpe (dybdeskarphet).",
  lens: "🔍 Passer til dokumenter, brev, segl og mynter. Rammer inn detaljen i en sirkulær lupe-ring.",
};

export const WysiwygKenBurnsHud = ({
  hudRef,
  imageUrl,
  currentFocus,
  currentZoom,
  currentEffect = "none",
  onZoomChange,
  onFocusClick,
  onEffectChange,
  onReset,
  onClose,
}) => {
  const t = useScrollytellTranslation();

  const handleThumbnailClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / (rect.width || 1)));
    const relY = Math.max(0, Math.min(1, (e.clientY - rect.top) / (rect.height || 1)));
    const newX = relX * 2 - 1;
    const newY = 1 - relY * 2;
    onFocusClick?.(newX, newY);
  };

  return (
    <div
      ref={hudRef}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid="kenburns-hud"
      style={{
        position: "fixed",
        bottom: 84,
        right: 24,
        width: 320,
        zIndex: 1250,
        backgroundColor: "#FFFFFF",
        borderRadius: 8,
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        padding: 16,
        cursor: "default",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#0F172A" }}>
          {t("imageFocusZoomPicker.title", "WYSIWYG Ken Burns: Klikk i bakgrunnsbildet for å velge fokus")}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label={t("common.close", "Lukk")}>
          <Close fontSize="small" />
        </IconButton>
      </div>

      <div
        style={{
          display: "inline-block",
          backgroundColor: "#F1F5F9",
          color: "#334155",
          padding: "4px 8px",
          borderRadius: 4,
          fontSize: "11px",
          fontWeight: 600,
          fontFamily: "monospace",
        }}
      >
        X: {currentFocus.x.toFixed(2)} | Y: {currentFocus.y.toFixed(2)} | Zoom: {currentZoom.toFixed(2)}x
      </div>

      {imageUrl && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#F8FAFC",
            borderRadius: "4px",
            border: "1px solid #CBD5E1",
            padding: "4px",
          }}
        >
          <div
            onClick={handleThumbnailClick}
            style={{
              position: "relative",
              display: "inline-block",
              cursor: "crosshair",
              lineHeight: 0,
            }}
            data-testid="kenburns-mini-thumbnail"
          >
            <img
              alt="Preview"
              src={resolveImageSrc(imageUrl)}
              style={{
                maxWidth: "100%",
                maxHeight: "140px",
                width: "auto",
                height: "auto",
                display: "block",
                borderRadius: "2px",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: `${((currentFocus.x + 1) / 2) * 100}%`,
                top: `${((1 - currentFocus.y) / 2) * 100}%`,
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: "#4484F1",
                border: "2px solid #FFFFFF",
                boxShadow: "0 0 4px rgba(0,0,0,0.6)",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
      )}

      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "#475569" }}>
          {t("imageFocusZoomPicker.zoomLabel", "Zoom-nivå")}: {currentZoom.toFixed(2)}x
        </Typography>
        <Slider
          min={1.0}
          max={2.5}
          step={0.05}
          value={currentZoom}
          onChange={onZoomChange}
          size="small"
          aria-label={t("imageFocusZoomPicker.zoomLabel", "Zoom-nivå")}
        />
      </Box>

      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "#334155", display: "block", mb: 0.5 }}>
          {t("kenBurns.effectLabel", "Fokuseffekt (valgfri)")}
        </Typography>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {EFFECTS.map((eff) => {
            const isSelected = (currentEffect || "none") === eff.id;
            return (
              <button
                key={eff.id}
                type="button"
                data-testid={`kenburns-effect-btn-${eff.id}`}
                onClick={() => onEffectChange?.(eff.id)}
                style={{
                  fontSize: "11px",
                  padding: "3px 8px",
                  borderRadius: "4px",
                  border: isSelected ? "1.5px solid #4484F1" : "1px solid #CBD5E1",
                  backgroundColor: isSelected ? "#EFF6FF" : "#FFFFFF",
                  color: isSelected ? "#1D4ED8" : "#475569",
                  fontWeight: isSelected ? 600 : 500,
                  cursor: "pointer",
                  transition: "all 120ms ease",
                }}
              >
                {t(eff.labelKey, eff.defaultLabel)}
              </button>
            );
          })}
        </div>
        <div
          data-testid="kenburns-effect-description"
          style={{
            marginTop: 6,
            padding: "6px 8px",
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: 4,
            fontSize: "11px",
            lineHeight: "15px",
            color: "#475569",
          }}
        >
          {t(`kenBurns.descriptions.${currentEffect || "none"}`, EFFECT_DESCRIPTIONS[currentEffect || "none"])}
        </div>
      </Box>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
        <Button size="small" variant="outlined" onClick={onReset} data-testid="kenburns-reset-btn">
          {t("imageFocusZoomPicker.reset", "Nullstill")}
        </Button>
        <Button size="small" variant="contained" onClick={onClose} sx={{ backgroundColor: "#4484F1" }} data-testid="kenburns-done-btn">
          {t("imageFocusZoomPicker.done", "Ferdig")}
        </Button>
      </div>
    </div>
  );
};
